import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { RootState } from "../../store";
import { RequestResults } from "../../common/RequestStatus";

export interface Campaign {
    id: string;
    label: string;
    urlPattern: string;
    currency: string;
    createdAt: string;
}

interface CampaignState {
    data: Campaign[];
    status: RequestResults;
    error: string | null;
}

const initialState: CampaignState = {
    data: [],
    status: RequestResults.idle,
    error: null,
};

export const getAllCampaigns = createAsyncThunk<
    Campaign[],
    void,
    { rejectValue: string }
>("campaign/getAll", async (_, { rejectWithValue }) => {
    try {
        const response = await api.get<{ data: Campaign[] }>(apiEndpoints.campaign.getAll);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || "Failed to fetch campaigns");
    }
});

export const createCampaign = createAsyncThunk<
    Campaign,
    { label: string; urlPattern: string; currency?: string },
    { rejectValue: string }
>("campaign/create", async (body, { rejectWithValue }) => {
    try {
        const response = await api.post<{ data: Campaign }>(apiEndpoints.campaign.create, { data: body });
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || "Failed to create campaign");
    }
});

export const updateCampaign = createAsyncThunk<
    Campaign,
    { id: string; label: string; urlPattern: string; currency?: string },
    { rejectValue: string }
>("campaign/update", async ({ id, ...body }, { rejectWithValue }) => {
    try {
        const response = await api.put<{ data: Campaign }>(`${apiEndpoints.campaign.update}/${id}`, { data: body });
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || "Failed to update campaign");
    }
});

export const deleteCampaign = createAsyncThunk<
    string,
    string,
    { rejectValue: string }
>("campaign/delete", async (id, { rejectWithValue }) => {
    try {
        await api.delete<any>(`${apiEndpoints.campaign.delete}/${id}`);
        return id;
    } catch (error: any) {
        return rejectWithValue(error.message || "Failed to delete campaign");
    }
});

const campaignSlice = createSlice({
    name: "campaign",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAllCampaigns.pending, (state) => { state.status = RequestResults.pending; })
            .addCase(getAllCampaigns.fulfilled, (state, action) => {
                state.data = action.payload;
                state.status = RequestResults.success;
            })
            .addCase(getAllCampaigns.rejected, (state, action) => {
                state.status = RequestResults.failed;
                state.error = action.payload || "Failed";
            })
            .addCase(createCampaign.fulfilled, (state, action) => {
                state.data.unshift(action.payload);
            })
            .addCase(updateCampaign.fulfilled, (state, action) => {
                const idx = state.data.findIndex((c) => c.id === action.payload.id);
                if (idx !== -1) state.data[idx] = action.payload;
            })
            .addCase(deleteCampaign.fulfilled, (state, action) => {
                state.data = state.data.filter((c) => c.id !== action.payload);
            });
    },
});

export const selectCampaigns = (state: RootState) => state.campaign;

export default campaignSlice.reducer;
